import { useState } from 'react';
import { useTable, type ColumnDef } from '@tanstack/react-table';
import { MdOutlineClose } from 'react-icons/md';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { Tag } from '@/components/Tag';
import { DataTable, dataTableFeatures } from '@/components/ui/data-table';
import { useDebounce } from '@/hooks/useDebounce';
import useFetchActiveUsers from '../../api/useFetchActiveUsers';
import useCopySourceCandidates from '../hooks/useCopySourceCandidates';
import { formatLastOfferedAt } from '../utils/formatLastOfferedAt';
import CandidatesJobPostEndpointsCopyJobPostsContents from './CandidatesJobPostEndpointsCopyJobPostsContents';
import type {
  AgentApiCandidateEndpointsCopySourceCandidate,
  AgentApiProvidedJobPostsRecord,
} from '@/types/serializers';

type Props = {
  candidateProfileId: number;
  sourceCandidate: AgentApiCandidateEndpointsCopySourceCandidate | null;
  setSourceCandidate: (candidate: AgentApiCandidateEndpointsCopySourceCandidate | null) => void;
  selectedJobPosts: AgentApiProvidedJobPostsRecord[];
  setSelectedJobPosts: (jobPosts: AgentApiProvidedJobPostsRecord[]) => void;
};

export default function CandidatesJobPostEndpointsCopyContents({
  candidateProfileId,
  sourceCandidate,
  setSourceCandidate,
  selectedJobPosts,
  setSelectedJobPosts,
}: Props) {
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [assigneeId, setAssigneeId] = useState<number | undefined>(undefined);
  const keywordDebounce = useDebounce(300);

  const { data: activeUsersData } = useFetchActiveUsers();
  const { data, isLoading } = useCopySourceCandidates({
    keyword,
    assignee_id: assigneeId,
    candidate_profile_id: candidateProfileId,
  });

  const candidates = data?.records ?? [];

  const assigneeItems = [
    { value: 'all', label: 'すべて' },
    ...(activeUsersData?.records.map((user) => ({ value: String(user.id), label: user.name })) ??
      []),
  ];

  const hasSearchConditions = keywordInput !== '' || assigneeId != null;

  const handleClearSearch = () => {
    setKeywordInput('');
    setKeyword('');
    setAssigneeId(undefined);
    keywordDebounce(() => setKeyword(''));
  };

  const columns: ColumnDef<
    typeof dataTableFeatures,
    AgentApiCandidateEndpointsCopySourceCandidate
  >[] = [
    {
      id: 'candidate',
      header: '求職者氏名 / 担当者',
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-xs">
          <button
            type="button"
            className="truncate text-left font-bold text-main focus-visible:underline"
            onClick={(e) => {
              e.stopPropagation();
              setSourceCandidate(row.original);
            }}
          >
            {row.original.name}
            {row.original.age != null && `(${row.original.age})`}
          </button>
          {row.original.assignees.length > 0 && (
            <Tag color="default" size="sm" className="shrink-0">
              {row.original.assignees[0].name}
              {row.original.assignees.length > 1 && (
                <span className="ml-xxs font-bold text-primary">
                  {`+${row.original.assignees.length - 1}`}
                </span>
              )}
            </Tag>
          )}
        </div>
      ),
      meta: { headerClassName: 'w-[27%]', cellClassName: 'w-[27%]' },
    },
    {
      id: 'job_category',
      header: '職種',
      accessorFn: (row) => row.job_category ?? '-',
      meta: { headerClassName: 'w-[26%]', cellClassName: 'w-[26%] text-main' },
    },
    {
      id: 'offered_count',
      header: '提案求人',
      accessorFn: (row) => (row.offered_count > 0 ? `${row.offered_count}件` : '-'),
      meta: { headerClassName: 'w-[30%]', cellClassName: 'w-[30%] text-main' },
    },
    {
      id: 'last_offered_at',
      header: '最終提案日時',
      accessorFn: (row) => (row.last_offered_at ? formatLastOfferedAt(row.last_offered_at) : '-'),
      meta: { headerClassName: 'w-[17%]', cellClassName: 'w-[17%] text-main' },
    },
  ];

  const table = useTable({
    features: dataTableFeatures,
    data: candidates,
    columns,
  });

  if (sourceCandidate) {
    return (
      <CandidatesJobPostEndpointsCopyJobPostsContents
        sourceCandidate={sourceCandidate}
        onBack={() => setSourceCandidate(null)}
        selectedJobPosts={selectedJobPosts}
        setSelectedJobPosts={setSelectedJobPosts}
      />
    );
  }

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-end gap-lg bg-gray p-md">
        <div className="flex flex-1 flex-col gap-xs">
          <label className="text-heading-xxs">フリーワード検索</label>
          <Input
            search
            className="w-full"
            placeholder="氏名・職種で絞り込む"
            value={keywordInput}
            onChange={(e) => {
              const value = e.target.value;
              setKeywordInput(value);
              keywordDebounce(() => setKeyword(value));
            }}
          />
        </div>
        <div className="flex w-[319px] flex-col gap-xs">
          <label className="text-heading-xxs">担当者</label>
          <Select
            className="w-full"
            items={assigneeItems}
            value={assigneeId ? String(assigneeId) : 'all'}
            onValueChange={(value) => setAssigneeId(value === 'all' ? undefined : Number(value))}
          />
        </div>
        {hasSearchConditions && (
          <Button color="tertiary" size="md" type="button" onClick={handleClearSearch}>
            <MdOutlineClose className="size-icon-xs text-primary" />
            <p className="text-sm font-bold text-primary">検索条件をクリア</p>
          </Button>
        )}
      </div>
      {!isLoading && candidates.length === 0 ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-lg">
          <p className="text-base font-bold text-main">求職者データがありません</p>
          <img src="/agent/candidates/candidate-no-data.svg" width={148} height={144} alt="" />
        </div>
      ) : (
        <DataTable
          table={table}
          isLoading={isLoading}
          skeletonRowCount={12}
          tableClassName="table-fixed w-full"
          containerClassName="rounded-none"
          onRowClick={(candidate) => setSourceCandidate(candidate)}
        />
      )}
    </div>
  );
}
